<template>
  <div
    class="!pl-4 cursor-pointer flex flex-row items-center gap-3 !justify-between dropdown_item dashboard_item_row"
    :class="{ selected: isSelected || isDropdownOpen }"
    @click="$emit('click', dashboard)"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="flex flex-row items-center gap-2 truncate">
      <ThemeImage
        class="size-4"
        :icon="imageNames.PANEL_ANALYTICS"
        alt="panel_analytics_icon"
      />

      <span class="text-sm color_666 font-normal truncate">{{
        renderEntity(dashboard)
      }}</span>
    </div>

    <div
      class="flex flex-row items-center gap-1 dashboard_item_actions"
      :class="{ show: isDropdownOpen || isHovered }"
    >
      <div
        v-for="(action, index) in actions"
        :key="`${action?.id}_action_${index}`"
        @click.stop="handleActionClick(action)"
      >
        <a-dropdown
          v-if="action?.options?.length > 0"
          trigger="click"
          placement="bottomLeft"
          :get-popup-container="(trigger) => trigger.parentElement"
          @visibleChange="handleDropdownChange($event, action.id)"
        >
          <a-tooltip placement="top" color="var(--tooltip-bg)">
            <template #title>
              <span class="text-[13px] font-normal primary_color">{{
                action?.title
              }}</span>
            </template>
            <div
              class="size-4 flex items-center justify-center cursor-pointer !rounded icon_wrapper"
              :class="{ selected: activeDropdown === action.id }"
            >
              <ThemeImage
                class="size-4"
                :icon="action?.icon"
                :alt="`${action?.id}_icon`"
              />
            </div>
          </a-tooltip>

          <template #overlay>
            <a-menu class="dropdown_menu">
              <a-menu-item
                v-for="(option, oIndex) in action?.options"
                :key="`${option?.id}_option_${oIndex}`"
                @click="handleOptionClick(option, dashboard)"
              >
                <div class="flex flex-row items-center gap-2">
                  <ThemeImage
                    class="size-4"
                    :icon="option?.icon"
                    :alt="`${option?.id}_icon`"
                  />
                  <span class="text-sm primary_color">{{ option?.title }}</span>
                </div>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>

        <a-tooltip
          v-else-if="action?.tooltip"
          placement="top"
          color="var(--tooltip-bg)"
        >
          <template #title>
            <span class="text-[13px] font-normal primary_color">{{
              action?.title
            }}</span>
          </template>
          <ThemeImage
            class="size-4"
            :icon="action?.icon"
            :alt="`${action?.id}_icon`"
          />
        </a-tooltip>
      </div>
    </div>

    <ConfirmationModal
      v-model:visible="showDeleteModal"
      title="Delete Dashboard"
      :message="`Are you sure you want to delete ${dashboard?.name} dashboard?`"
      :loading="isDeleting"
      type="delete"
      @confirm="handleDeleteConfirm"
      @cancel="showDeleteModal = false"
      confirm-text="Delete"
      loading-text="Deleting..."
    />

    <AddOrEditAnalyticsDashboardModal
      source="dashboard_item_rename"
      :visible="showRenameModal"
      :dashboard="dashboard"
      :space="null"
      mode="edit"
      @update:visible="showRenameModal = $event"
    />
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { renderEntity } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  getDashboardItemActions,
  dashboardEvents,
} from "@/src/constants/analyticConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import AddOrEditAnalyticsDashboardModal from "@/src/components/spaces_components/dashboards/AddOrEditAnalyticsDashboardModal.vue";

const router = useRouter();

const props = defineProps({
  dashboard: { type: Object, required: true },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits(["click", "action-click", "option-click"]);

const activeDropdown = ref(null);
const isDropdownOpen = ref(false);
const isHovered = ref(false);
const showDeleteModal = ref(false);
const showRenameModal = ref(false);
const isDeleting = ref(false);

const actions = getDashboardItemActions(imageNames, {
  onRename: (dashboard) => {
    activeDropdown.value = null;
    isDropdownOpen.value = false;
    isHovered.value = false;

    handleRenameDashboard(dashboard);
  },

  onOpenInNewTab: (dashboard) => {
    activeDropdown.value = null;
    isDropdownOpen.value = false;
    isHovered.value = false;

    const routeData = router.resolve({
      name: "analytics-dashboard",
      params: { dashboard_id: dashboard.id },
    });

    window.open(routeData.href, "_blank");
  },

  onDelete: (dashboard) => {
    activeDropdown.value = null;
    isDropdownOpen.value = false;
    isHovered.value = false;

    showDeleteModal.value = true;
  },
});

const handleActionClick = (action) => {
  try {
    if (!action?.options?.length && action?.action) {
      action.action();
    }
    emit("action-click", action);
  } catch (error) {
    console.error("An error occurred while handling action click: ", error);
  }
};

const handleOptionClick = (option, dashboard) => {
  try {
    if (option?.action) {
      option.action(dashboard);
    }
    emit("option-click", option, dashboard);
  } catch (error) {
    console.error("An error occurred while handling option click: ", error);
  }
};

const handleMouseEnter = () => {
  isHovered.value = true;
};

const handleMouseLeave = () => {
  isHovered.value = false;
};

const handleDropdownChange = (visible, actionId) => {
  try {
    if (visible) {
      activeDropdown.value = actionId;
      isDropdownOpen.value = true;
    } else {
      if (activeDropdown.value === actionId) {
        activeDropdown.value = null;
        isDropdownOpen.value = false;
      }
    }
  } catch (error) {
    console.error("An error occurred while handling dropdown change: ", error);
  }
};

const handleRenameDashboard = (dashboard) => {
  activeDropdown.value = null;
  isDropdownOpen.value = false;
  isHovered.value = false;
  showRenameModal.value = true;
};

const handleDeleteConfirm = async () => {
  try {
    isDeleting.value = true;

    const response = await AnalyticService.V3.deleteDashboard(
      props.dashboard?.id,
    );
    if (response?.success) {
      message.success(
        `Dashboard ${props.dashboard?.name} deleted successfully!`,
      );
      showDeleteModal.value = false;
      eventBus.emit("dashboard-manipulated", dashboardEvents.DELETED);
    }
  } catch (error) {
    console.error("An error occurred while deleting a dashboard: ", error);
    message.error("An error occurred while deleting dashboard!");
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped>
.dashboard_item_actions {
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
}

.dashboard_item_actions.show {
  opacity: 1;
  visibility: visible;
}
</style>
